import { CreateTransient, Icon } from "@mindlercare/ui-web";
import { differenceInDays, format, isPast, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";

interface Props {
  feedbackDate?: string | null;
  feedbackDateIgnored: boolean;
  hasFeedback?: boolean;
}

const DateText = ({
  date,
  past,
  hasFeedback,
}: {
  date: string | number | undefined | null;
  past: boolean;
  hasFeedback?: boolean;
}) => {
  const { t } = useTranslation();

  if (!date) {
    return (
      <Typography insensitive variant="body-small">
        {"--"}
      </Typography>
    );
  }

  const feedbackDate = new Date(date);
  const currentDate = new Date();

  const daysPast = differenceInDays(currentDate, new Date(feedbackDate));

  return (
    <Typography
      insensitive
      variant="body-small"
      color={past ? "ERROR" : undefined}
    >
      {past && !hasFeedback
        ? t("OVERDUE", { days: daysPast })
        : format(feedbackDate, "yyyy-MM-dd")}
    </Typography>
  );
};

export const FeedbackDateCellText = ({
  feedbackDate,
  hasFeedback,
  feedbackDateIgnored,
}: Props) => {
  const date = new Date(feedbackDate ?? "");
  const isPastAndNotToday = isPast(date) && !isToday(date);
  const isNotAnsweredFeedback =
    !hasFeedback && feedbackDate && !feedbackDateIgnored;
  const shouldHighlight = isNotAnsweredFeedback ? isPastAndNotToday : false;
  return (
    <Wrapper>
      <CalendarIcon $noFeedback={shouldHighlight && !hasFeedback} />
      <Typography
        insensitive
        variant="body-small"
        color={shouldHighlight ? "ERROR" : "GRAY_BLACK"}
      >
        <DateText
          date={feedbackDate}
          past={shouldHighlight}
          hasFeedback={hasFeedback}
        />
      </Typography>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CalendarIcon = styled(Icon.Calendar)<
  CreateTransient<{ noFeedback: boolean }>
>`
  width: 20px;
  color: ${({ theme, $noFeedback }) =>
    $noFeedback ? theme.COLORS.ERROR : theme.COLORS.GRAY};
`;
